import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { X, MapPin, ArrowRight } from 'lucide-react';
import { useUserState, STATE_NAMES, isLiveState } from '../../lib/userState';
import { getStateEquivalent } from '../../data/stateVariantMap';

/**
 * AdelaideUrlHeadsUpStrip
 * ─────────────────────────────────────────────────────────────
 * Global, client-side, additive banner. Mounted once at the top of
 * <App> so we do not have to touch 100+ Adelaide-named page components
 * individually. Shown when BOTH of the below are true:
 *   1. The current pathname is an "Adelaide-named" URL (contains
 *      /adelaide, /south-australia, /-adelaide or similar SA-locked
 *      slugs), AND
 *   2. The visitor has picked a state that is NOT South Australia.
 *
 * SEO safety
 * ──────────
 * - No <meta>, <link rel="canonical"> or `robots` tag is changed. The
 *   original SA-focused page HTML/H1/schema still renders exactly as
 *   Google crawls it today.
 * - The strip is client-side additive DOM; server-rendered / SSR'd
 *   HTML is unaffected. Googlebot does execute JS on second render,
 *   but the strip introduces neither duplicate content, boilerplate
 *   duplication (its ~120 characters are the same everywhere and
 *   never touch <title> / <h1>), nor a canonical conflict.
 * - Dismissible via localStorage so a returning VIC user is not
 *   nagged on every SA URL.
 *
 * User-side value
 * ───────────────
 * A VIC visitor who ends up on `/first-home-buyer-adelaide` from a
 * Google result now sees a clear "Heads up - SA-focused. See our VIC
 * page instead" prompt at the very top, rather than reading an
 * Adelaide guide thinking it applies to Melbourne.
 */

// Regex list of URL fragments that mark a page as Adelaide-/SA-locked.
// Case-insensitive. We deliberately do NOT match /vic/*, /waitlist,
// /workspace, /help/cheapest-land-south-australia is included because
// that page IS SA-locked. `/vic` first-segment excluded upstream via
// startsWith('/vic') check.
const SA_URL_PATTERNS = [
  /(?:^|\/)adelaide(?:$|[/-])/i,          // /adelaide, /adelaide-something, /home-builders-adelaide
  /-adelaide(?:$|\/)/i,                    // /home-builders-adelaide, /suburbs-boom-adelaide
  /-adelaide-/i,                           // /-adelaide-something-
  /(?:^|\/)south-australia(?:$|[/-])/i,   // /south-australia, /south-australia/xyz
  /-south-australia(?:$|\/)/i,             // /cheapest-land-south-australia
];

const DISMISS_KEY = 'bp:sa-headsup-dismissed-v1';

const isSaLockedUrl = (pathname) => {
  if (!pathname) return false;
  // Never fire on Victoria pages, waitlist, workspace or auth routes.
  if (pathname.startsWith('/vic')) return false;
  if (pathname.startsWith('/workspace')) return false;
  if (pathname.startsWith('/waitlist')) return false;
  if (pathname.startsWith('/hq')) return false;
  return SA_URL_PATTERNS.some((re) => re.test(pathname));
};

const AdelaideUrlHeadsUpStrip = () => {
  const { pathname } = useLocation();
  const userState = useUserState();
  const [dismissed, setDismissed] = useState(false);

  // Re-check dismissal per route change so it stays dismissed across
  // internal nav until the user manually re-enables it.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === '1');
    } catch (_) { /* private mode / storage full: keep visible */ }
  }, [pathname]);

  const stateName = STATE_NAMES[userState] || userState;
  const shouldShow =
    userState && userState !== 'SA' && isSaLockedUrl(pathname) && !dismissed;

  if (!shouldShow) return null;

  const onDismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* noop */ }
  };

  // VIC visitors see a CTA that routes to the EXACT Melbourne equivalent
  // of this page when one exists, otherwise to the Victoria hub. Every
  // other non-live state routes into the Notify Me waitlist page.
  const vicEquivalent = userState === 'VIC' ? getStateEquivalent(pathname, 'VIC') : null;
  const hasExactVariant = vicEquivalent && vicEquivalent !== '/vic';
  const ctaHref = userState === 'VIC'
    ? (vicEquivalent || '/vic')
    : userState ? `/waitlist?state=${userState}` : '/waitlist';
  const ctaLabel = userState === 'VIC'
    ? (hasExactVariant ? 'See the Melbourne version' : 'View Victoria edition')
    : `Join ${stateName} waitlist`;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[60] bg-[#0d3d1e] text-white border-t border-[#0d3d1e]/40 shadow-[0_-6px_20px_rgba(0,0,0,0.15)]"
      role="region"
      aria-label="Heads up: this page is South Australia focused"
      data-testid="adelaide-url-headsup"
      data-user-state={userState}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 flex items-center gap-3 sm:gap-4">
        <MapPin className="w-4 h-4 shrink-0 opacity-85" aria-hidden="true" />
        <p className="flex-1 text-[12.5px] sm:text-[13px] leading-[1.4]">
          <span className="font-semibold">Heads up:</span>{' '}
          <span className="text-white/90">
            you picked {stateName}.<span className="hidden sm:inline"> This page is South Australia focused.</span>
          </span>
        </p>
        <Link
          to={ctaHref}
          className="inline-flex items-center gap-1 text-[12px] sm:text-[12.5px] font-semibold text-white bg-white/10 hover:bg-white/20 rounded-full px-2.5 sm:px-3 py-1.5 transition-colors whitespace-nowrap"
          data-testid="adelaide-url-headsup-cta"
        >
          {ctaHref === '/vic' || !userState || userState !== 'VIC' ? (
            <span>{ctaLabel}</span>
          ) : (
            <>
              <span className="sm:hidden">Melbourne version</span>
              <span className="hidden sm:inline">{ctaLabel}</span>
            </>
          )}
          <ArrowRight className="w-3 h-3" />
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Dismiss"
          data-testid="adelaide-url-headsup-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default AdelaideUrlHeadsUpStrip;
